import { RootState } from "../root/rootState"
import { DossierPage, PageWithBlocks } from "../root/cmsPage/cmsPageState"
import { Option } from 'widgets-for-react'
import { FormFieldMapping, getAdditionalQuestionSettings, getFieldValue, getFormConfig } from "../root/mailPlusForm/formCategories"

export type SqzlEvent =
  PageViewEvent
  | ViewContentEvent
  | LeadEvent
  | CompleteRegistrationEvent
  | EmailOptinEvent
  | ViewReadEvent

export type PageViewEvent = {
  event: 'PageView'
  campaign?: string | undefined
  source?: string | undefined
}

export type ViewContentEvent = {
  event: 'ViewContent'
  campaign?: string | undefined
  source?: string | undefined
  currency?: string | undefined
  language?: string | undefined
  category_id?: string | undefined
  checkin_date?: string | undefined
  checkout_date?: string | undefined
  amount_nights?: number | undefined
  products: ProductData[]
}

export type ViewReadEvent = {
  event: 'ContentRead'
  category_id?: string | undefined
}

export type LeadEvent = {
  event: 'Lead'
  email?: string | undefined
  campaign?: string | undefined
  source?: string | undefined
  value?: string | undefined
}

export type CompleteRegistrationEvent = {
  event: 'CompleteRegistration'
  email?: string | undefined
  campaign?: string | undefined
  source?: string | undefined
}

export type EmailOptinEvent = {
  event: 'EmailOptIn'
  email?: string | undefined
  newsletter: 'yes' | 'no'
  marketing?: 'yes' | 'no' | undefined
  service?: 'yes' | 'no' | undefined
}

export type CollectionData = {
  collections: {
    customer_settings: Record<string, string | string[] | Record<string, string>>
  }
}

type ProductData = {
  id: string
  name: string
  price: number
  category_ids: string[]
  language: string
}

export type CustomPersonalData = {
  custom_email?: string
  custom_firstname?: string
  custom_tussenvoegsel?: string
  custom_lastname?: string
  custom_functie?: string
  custom_phone?: string
  custom_company?: string
}

export type CustomFormData = {
  custom_type_aanvraag?: string
  custom_form_name?: string
  custom_form_code?: string
}

export type CustomDossierData = {
  custom_hoofddossier?: string
  custom_dossier?: string
}

/**
 * Triggers a SQZL view event
 * @param state
 */
export const SpotlerViewEvent = () => {
  const sqzlData: SqzlEvent = { event: 'PageView' }
  pushSqzl(sqzlData)
}

/**
 * Triggers a SQZL ViewRead event for CMS content
 * @param contentType
 * @param contentId
 */
export const SpotlerCMSEvent = (pageState: PageWithBlocks) => {
  const pageType = pageState.kind
  switch (pageType) {
    case 'dossier':
    case 'knowledge-article':
    case 'actuality-details':
      const viewContentData: ViewReadEvent & CustomDossierData = { event: 'ContentRead' }
      viewContentData.category_id = "kennis"
      viewContentData.custom_dossier = pageState.title
      const masterDossier = findMasterDossier(pageState.primaryDossier)
      if (masterDossier) {
        viewContentData.custom_hoofddossier = masterDossier.title
      }
      pushSqzl(viewContentData)
      break
  }
}

export const SpotlerFormEvent = (formId: number, formName: string) => {
  const config = getFormConfig(formId);
  if (!config) {
    console.error('Unsupported form ID for pushing SQZL event:', formId);
    return
  }
  const fields: FormFieldMapping = config.fields;

  try {
    const additionalQuestions = config.additionalQuestions ? getAdditionalQuestionSettings(config.additionalQuestions) : {};
    switch (config.event) {
      case 'Lead':
      case 'CompleteRegistration':
        const leadData: SqzlEvent & CustomPersonalData & CustomFormData & CollectionData = {
          event: config.event,
          custom_email: getFieldValue(fields.email),
          custom_firstname: getFieldValue(fields.firstName),
          custom_tussenvoegsel: getFieldValue(fields.insertion),
          custom_lastname: getFieldValue(fields.lastName),
          custom_functie: getFieldValue(fields.function),
          custom_phone: getFieldValue(fields.phone),
          custom_company: getFieldValue(fields.company),
          custom_type_aanvraag: config.type,
          custom_form_name: formName ?? config.name,
          custom_form_code: String(formId),
          collections: {
            customer_settings: { ...additionalQuestions }
          }
        }
        pushSqzl(leadData);
        break;
      case 'EmailOptIn':
        const emailOptInData: EmailOptinEvent & CustomPersonalData & CustomFormData & CollectionData = {
          event: config.event,
          email: getFieldValue(fields.email),
          custom_firstname: getFieldValue(fields.firstName),
          custom_tussenvoegsel: getFieldValue(fields.insertion),
          custom_lastname: getFieldValue(fields.lastName),
          custom_functie: getFieldValue(fields.function),
          custom_phone: getFieldValue(fields.phone),
          custom_company: getFieldValue(fields.company),
          custom_type_aanvraag: config.type,
          custom_form_name: formName ?? config.name,
          newsletter: 'yes',
          custom_form_code: String(formId),
          collections: {
            customer_settings: { ...additionalQuestions }
          }
        }
        pushSqzl(emailOptInData);
        break;
    }
  } catch (error) {
    console.error('Error pushing SQZL event:', error);
  }
}

const findMasterDossier = (dossier: Option<DossierPage>): DossierPage | undefined => {
  if (dossier.kind == 'r') {
    if (dossier.v.primaryDossier.kind === 'r') {
      return findMasterDossier(dossier.v.primaryDossier)
    }
    return dossier.v
  }
  return undefined
}

export const pushSqzl = (props: SqzlEvent) => {
  const params = { ...props, custom_location: window.location.pathname, url: window.location.href }
  console.groupCollapsed('pushSqzl:', params)
  console.debug(params)
  console.groupEnd()

  if (window._sqzl) window._sqzl.push(params)
}
